// label.ts — LOCAL parse of nutrition-label OCR text. NO AI call for a printed label:
// the photo is OCR'd on-device (Apple Vision, ocr.rs) and these regexes lift the numbers.
// Only when this parse fails does the UI OFFER the one billed brain text call
// (brain.parseLabelWithAI) as a fallback — the photo itself never leaves the device.
//
// Handles the common US ("Nutrition Facts") and EU ("Energy … kJ / kcal, per 100 g /
// per serving") label shapes, loose OCR spacing, and comma decimals.

export interface LabelParse {
  calories: number | null; // kcal
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  /** "serving" | "100g" | null — which basis the parsed figures most likely describe. */
  basis: "serving" | "100g" | null;
}

const num = (s: string): number | null => {
  const v = Number(s.replace(",", "."));
  return Number.isFinite(v) && v >= 0 ? v : null;
};

/** First capture group of the first matching pattern, as a number. */
function firstNum(text: string, patterns: RegExp[]): number | null {
  for (const re of patterns) {
    const m = re.exec(text);
    if (m) {
      const v = num(m[1]);
      if (v !== null) return v;
    }
  }
  return null;
}

export function parseNutritionLabel(raw: string): LabelParse {
  // Normalize: collapse whitespace, unify decimal commas inside numbers handled per-match.
  const text = raw.replace(/\s+/g, " ").trim();

  // Calories: "Calories 240", "240 Calories", "Energy 1046 kJ / 250 kcal", "250 kcal".
  let calories = firstNum(text, [
    /calories[^0-9]{0,12}(\d{1,5})/i,
    /(\d{1,5})\s*calories/i,
    /(\d{1,4}(?:[.,]\d)?)\s*kcal/i,
  ]);
  // A kJ-only label: convert (1 kcal = 4.184 kJ).
  if (calories === null) {
    const kj = firstNum(text, [/(\d{2,5}(?:[.,]\d)?)\s*k[jJ]\b/]);
    if (kj !== null) calories = Math.round(kj / 4.184);
  }
  if (calories !== null) calories = Math.round(calories);

  const protein = firstNum(text, [
    /protein[^0-9]{0,12}(\d{1,4}(?:[.,]\d)?)\s*g/i,
    /protein[^0-9]{0,12}(\d{1,4}(?:[.,]\d)?)/i,
  ]);
  const carbs = firstNum(text, [
    /(?:total\s+)?carbohydrates?[^0-9]{0,12}(\d{1,4}(?:[.,]\d)?)\s*g/i,
    /carb(?:s|ohydrate)?[^0-9]{0,12}(\d{1,4}(?:[.,]\d)?)/i,
  ]);
  const fat = firstNum(text, [
    /(?:total\s+)?fat[^0-9]{0,12}(\d{1,4}(?:[.,]\d)?)\s*g/i,
  ]);

  let basis: LabelParse["basis"] = null;
  if (/per\s*100\s*(?:g|ml)/i.test(text) && !/per\s+serving|serving size/i.test(text)) basis = "100g";
  else if (/per\s+serving|serving size|amount per serving/i.test(text)) basis = "serving";
  else if (/100\s*(?:g|ml)/i.test(text)) basis = "100g";

  return { calories, protein_g: protein, carbs_g: carbs, fat_g: fat, basis };
}

/** A parse is "usable" when at least the calorie figure was found. */
export function labelParseUsable(p: LabelParse): boolean {
  return p.calories !== null && p.calories > 0;
}
